import axios from 'axios'
import {createAsyncThunk, PayloadAction, createAction, Action} from '@reduxjs/toolkit'
import { sliceStateStatus, systemStatus } from '@/Core';
import {routesIndex} from '@/RoutesIndex';

import ISliceState from '@/interface/core/ISliceState';
import { generatePaginationStateDataItem, generateBmsStateDataItem } from '../../../hooks'; 
import IBulkMembershipStock from '@/interface/IBulkMembershipStock';
import IPagination from '@/interface/core/IPagination';
import { IType } from '@/interface/IType';

/**
 * Stock di richieste massive
 * Recupera gli stock più recenti (max 100)
 *
 * @param {number} anno - Anno di riferimento
 * @param {string} regione - Regione del tesseratore
 * @param {string} stato - Stato (pending, completed, all)
 */

export const bmsIndex = createAsyncThunk(
  'bms/index',
  async (_data: { anno?: number; regione?: string; stato?: string }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix +  routesIndex.api.bulkMembershipStocks +   routesIndex.api.methods.index;

      const q = axios.post(uri, _data, config);
      const r = (await q).data;

      return setStateData(r); 
      
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Conta gli stock con richieste massive pending e il totale delle richieste da lavorare
 * @param {number | null} anno - Anno di riferimento (opzionale)
 */
export const bmsCountPending = createAsyncThunk(
  'bms/countPending',
  async (anno: number | null = null, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      const uri = routesIndex.api.prefix + routesIndex.api.bulkMembershipStocks + routesIndex.api.methods.count;

      const payload = anno ? { anno } : {};

      const response = await axios.post(uri, payload, config);

      return {
        stocksWithPending: response.data.stocks_with_pending,
        totalPendingRequests: response.data.total_pending_requests,
      };

    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


/**
 * Stock di richieste massive
 * Risultato paginato
 *
 * @param {number} anno - Anno di riferimento
 * @param {string} regione - Regione del tesseratore
 * @param {string} stato - Stato (pending, completed, all)
 * @param {number} pageSize - Numero risultati per pagina
 * @param {number} page - Numero pagina
 * @param {string} globalFilter - Filtro generale
 */

export const bmsPaginate = createAsyncThunk(
  'bms/paginate',
  async (_data: any, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      let uri = routesIndex.api.prefix + routesIndex.api.bulkMembershipStocks + routesIndex.api.methods.paginate;

      if (_data?.page && parseInt(_data.page) > 1) {
        uri += '?page=' + _data.page;
      }

      const q = axios.post(uri, _data, config);
      const r = (await q).data;

      return setStateData(r, true); 

    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Stock di richieste massive raggruppato per regione
 * Risultato paginato
 *
 * @param {number} anno - Anno di riferimento
 * @param {string} stato - Stato (pending, completed, all)
 * @param {number} pageSize - Numero risultati per pagina
 * @param {number} page - Numero pagina
 * @param {string} globalFilter - Filtro generale (su regione)
 */

export const bmsPaginateByRegion = createAsyncThunk(
  'bms/paginateByRegion',
  async (_data: any, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      let uri = routesIndex.api.prefix + routesIndex.api.bulkMembershipStocks + '/paginateByRegion';

      if (_data?.page && parseInt(_data.page) > 1) {
        uri += '?page=' + _data.page;
      }

      const q = axios.post(uri, _data, config);
      const r = (await q).data;

      return setStateData(r, true);

    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Visualizza uno stock di richieste massive
 *
 * @param {string | number} id - ID dello stock
 */
export const bmsView = createAsyncThunk(
  'bms/view',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };

      const uri = routesIndex.api.prefix + routesIndex.api.bulkMembershipStocks + '/' + id;

      const response = await axios.get(uri, config);

      return setStateData(response.data);

    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);





const setStateData = (apiResponse: any, paginate: boolean = false) => {
  let stateData: ISliceState = {
    data: [],
    status: sliceStateStatus.fulfilled,
  };

  if (apiResponse.status && apiResponse.result) {
    if (paginate) {
      stateData.data = {} as IPagination;
      Object.assign(stateData.data, generatePaginationStateDataItem(apiResponse.result, IType.bms)); 
    } else {
      if (Array.isArray(apiResponse.result)) {
        stateData.data = apiResponse.result.map((item: any) =>
          generateBmsStateDataItem(item) 
        );
      } else {
        stateData.data = generateBmsStateDataItem(apiResponse.result);
      }
    }
  }

  return stateData;
};